import { Icon, type IconName } from '@web/components/ui/icons'
import { type CSSProperties, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type ActionMenuItem = {
  id: string
  label: string
  description?: string
  icon?: IconName
  disabled?: boolean
  onSelect: () => void
}

/**
 * A round "+" trigger that opens a small menu of actions, the composer's
 * options. It shares the pill menu's surface and keyboard model (arrows,
 * Home/End, Escape back to the trigger) but holds commands, not a value.
 */
export function ActionMenu({
  label,
  items,
  disabled = false,
  icon = 'plus',
  className = 'btn btn-icon',
}: {
  label: string
  items: ActionMenuItem[]
  disabled?: boolean
  icon?: IconName
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<CSSProperties>({})
  const root = useRef<HTMLSpanElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const id = useId()
  const dismiss = (restore = true) => {
    setOpen(false)
    if (restore) trigger.current?.focus()
  }
  const place = () => {
    const bounds = trigger.current?.getBoundingClientRect()
    if (!bounds) return
    const width = Math.min(272, window.innerWidth - 16)
    const below = window.innerHeight - bounds.bottom - 16
    const desired = Math.min(320, 12 + items.length * 56)
    const downward = below >= desired || below >= bounds.top - 16
    setPosition({
      left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)),
      ...(downward
        ? { top: bounds.bottom + 8 }
        : { bottom: window.innerHeight - bounds.top + 8 }),
      width,
      maxHeight: Math.max(0, Math.min(320, downward ? below : bounds.top - 16)),
    })
  }
  useEffect(() => {
    if (!open) return
    menu.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')
      ?.focus()
    const onDown = (event: MouseEvent) => {
      if (
        !root.current?.contains(event.target as Node) &&
        !menu.current?.contains(event.target as Node)
      )
        setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        dismiss()
      }
    }
    const onResize = () => setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
    }
  }, [open])
  return (
    <span ref={root} className="relative inline-flex">
      <button
        ref={trigger}
        type="button"
        className={className}
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        disabled={disabled}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            place()
            setOpen(true)
          }
        }}
        onClick={() => {
          place()
          setOpen((value) => !value)
        }}
      >
        <Icon name={icon} size={15} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menu}
            id={id}
            role="menu"
            aria-label={label}
            className="pill-menu action-menu fixed z-[100] overflow-y-auto rounded-card border border-line p-1 shadow-card"
            style={position}
            onKeyDown={(event) => {
              const choices = Array.from(
                menu.current?.querySelectorAll<HTMLButtonElement>(
                  '[role="menuitem"]:not(:disabled)',
                ) ?? [],
              )
              const at = choices.indexOf(
                document.activeElement as HTMLButtonElement,
              )
              if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                event.preventDefault()
                const index =
                  event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? choices.length - 1
                      : (at +
                          (event.key === 'ArrowDown' ? 1 : -1) +
                          choices.length) %
                        choices.length
                choices[index]?.focus()
              } else if (event.key === 'Tab') dismiss()
            }}
          >
            <div className="label px-2 pb-1 pt-1.5">{label}</div>
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                aria-label={item.label}
                aria-describedby={
                  item.description ? `${id}-${item.id}-description` : undefined
                }
                disabled={item.disabled}
                className="pill-menu-option flex w-full items-start gap-2 rounded-control px-2 py-1.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  // Hand focus back to the trigger first, so a dialog the
                  // action opens can return there when it closes.
                  dismiss()
                  item.onSelect()
                }}
              >
                {item.icon && (
                  <Icon
                    name={item.icon}
                    size={16}
                    className="mt-0.5 text-muted"
                  />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-medium text-fg">
                    {item.label}
                  </span>
                  {item.description && (
                    <span
                      id={`${id}-${item.id}-description`}
                      className="block text-[11px] leading-snug text-muted"
                    >
                      {item.description}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>,
          root.current?.closest('[role="dialog"]') ?? document.body,
        )}
    </span>
  )
}
